import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import { useAppSelector } from '../../redux/hooks.ts'
import { selectTheme } from '../../features/setting/setting.slice.ts'
import { Theme } from '../../features/setting/setting.def.ts'
import {
  AgGridDensity,
  createDarkAgGridTheme,
  createLightAgGridTheme,
} from '../../styles/ag-grid/token.ts'
import {
  GetContextMenuItemsParams,
  MenuItemDef,
  ValueFormatterParams,
} from 'ag-grid-enterprise'
import { selectUser } from '../../features/user/user.slice.ts'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useFixedBreakpoint } from '../hooks/use-is-mobile.ts'

interface IdeyaAgGridTableProps extends AgGridReactProps {
  columns?: ColDef[]
  height?: number | string
  ref?: React.RefObject<AgGridReact | null>
  canCopy?: boolean
}

export const IdeyaAgGridTable: React.FC<IdeyaAgGridTableProps> = ({
  columns,
  height = 700,
  ref,
  getContextMenuItems,
  onGridReady,
  canCopy = false,
  ...agGridProps
}) => {
  const theme = useAppSelector(selectTheme)
  const userInfo = useAppSelector(selectUser)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { i18n } = useTranslation()
  const language = i18n.language

  // Compact density on PC/tablet (>= 768px); AG Grid default density on mobile.
  const screens = useFixedBreakpoint()
  const density: AgGridDensity = screens.md ? 'compact' : 'default'

  const agTheme = useMemo(
    () =>
      theme === Theme.Dark
        ? createDarkAgGridTheme(density)
        : createLightAgGridTheme(density),
    [theme, language, density]
  )

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
    }),
    []
  )

  const columnTypes = useMemo(
    () => ({
      numberColumn: {
        cellClass: 'ag-right-aligned-cell',
        headerClass: 'ag-right-aligned-header',
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value
            ? params.value.toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })
            : '0'
        },
      },
      textColumn: {
        cellClass: 'ag-left-aligned-cell',
        headerClass: 'ag-left-aligned-header',
      },
    }),
    []
  )

  // Request from PPS team to not allow export/copy table items for external users.
  // Currently if users have a userInfo.role, then they are internal users.
  const combinedGetContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => {
      let items: (string | MenuItemDef)[] = []

      if (typeof getContextMenuItems === 'function') {
        items = (getContextMenuItems(params) as MenuItemDef[]) ?? []
        if (userInfo.role) {
          const hasExport = items.some(
            (item) =>
              (typeof item === 'string' && item === 'export') ||
              (typeof item === 'object' && item.name === 'Export')
          )
          if (!hasExport) items.push('export')
        }
      } else if (params.defaultItems) {
        items = params.defaultItems
        if (!userInfo.role) {
          items = items.filter(
            (item) =>
              !(
                typeof item === 'string' &&
                (item === 'export' ||
                  item === 'csvExport' ||
                  item === 'excelExport')
              ) &&
              !(
                typeof item === 'object' &&
                (item.name === 'Export' ||
                  item.name === 'CSV Export' ||
                  item.name === 'Excel Export')
              )
          )
        }
      }

      if (!canCopy) {
        items = items.filter(
          (item) =>
            !(
              typeof item === 'string' &&
              (item === 'copy' ||
                item === 'copyWithHeaders' ||
                item === 'copyWithGroupHeaders')
            ) &&
            !(
              typeof item === 'object' &&
              (item.name === 'Copy' ||
                item.name === 'Copy with Headers' ||
                item.name === 'Copy with Group Headers')
            )
        )
      } else {
        // ensure basic 'copy' exists when canCopy is true
        const hasCopy = items.some(
          (item) =>
            (typeof item === 'string' && item === 'copy') ||
            (typeof item === 'object' && item.name === 'Copy')
        )
        if (!hasCopy) {
          items.push('copy')
        }
      }

      items = items.filter((item) => {
        const id =
          typeof item === 'string' ? item : ((item as MenuItemDef).name ?? '')

        return ![
          'cut',
          'paste',
          'Cut',
          'Paste',
          'separator',
          'Separator',
        ].includes(id)
      })

      return items as MenuItemDef[]
    },
    [getContextMenuItems, userInfo.role, canCopy]
  )

  return (
    <div ref={containerRef} style={{ height }}>
      <AgGridReact
        ref={ref}
        theme={agTheme}
        columnDefs={columns}
        rowModelType={'clientSide'}
        defaultColDef={defaultColDef}
        columnTypes={columnTypes}
        getContextMenuItems={combinedGetContextMenuItems}
        suppressDragLeaveHidesColumns={true}
        onGridReady={onGridReady}
        {...agGridProps}
      />
    </div>
  )
}
